import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FEATURE_AUDIO_FADE_CONTROL } from '../../flags';
import { AudioItem } from '../../items/audio/audio-item-type';
import { EditorStarterItem } from '../../items/item-type';
import {
  hasEmbeddedLipsyncAudio,
  VideoItem,
} from '../../items/video/video-item-type';
import { clsx } from '../../utils/clsx';
import { decibelToGain } from '../../utils/decibels';
import { usePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { getAudioData } from '../../utils/get-audio-data';
import { getVisibleFrames } from '../../utils/get-visible-frames';
import QuickLRU from '../../utils/quick-lru';
import {
  useAssetFromItem,
  useFps,
  useTimelineContext,
} from '../../utils/use-context';
import {
  AudioDataWithPeaks,
  generateDownsampledPeakData,
  selectPeakLevel,
} from '../../utils/waveform-peak-data';
import { FadeCurve } from './timeline-item-fade-control/fade-curve';
import { ITEM_COLORS } from '@/features/remotion-editor/editor/constants';

const CLIPPING_COLOR = '#FF7F50';

export const WAVEFORM_HEIGHT = 20;

export const getWaveformHeight = ({
  item,
  trackHeight,
}: {
  item: EditorStarterItem;
  trackHeight: number;
}) => {
  if (item.type === 'audio') {
    return trackHeight;
  }

  // Type safety check, add item types that don't have a waveform here
  if (
    item.type === 'video' ||
    item.type === 'captions' ||
    item.type === 'gif' ||
    item.type === 'webp' ||
    item.type === 'text' ||
    item.type === 'solid' ||
    item.type === 'blur' ||
    item.type === 'image' ||
    item.type === 'pdf' ||
    item.type === 'ppt' ||
    item.type === 'code' ||
    item.type === 'group'
  ) {
    return WAVEFORM_HEIGHT;
  }

  throw new Error('Invalid item type: ' + (item satisfies never));
};

const BAR_WIDTH = 1;
const BAR_GAP = 0;
const BAR_TOTAL = BAR_WIDTH + BAR_GAP;

/**
 * Optimized drawing function that uses pre-computed peak data
 */
function drawPeaks(
  canvas: HTMLCanvasElement,
  peaks: Float32Array,
  color: string,
  decibelAdjustment: number,
  width: number,
) {
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  // clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const height = canvas.height;
  const numBars = Math.ceil(width / BAR_TOTAL);

  // move expensive calculations outside the loop
  const volume = decibelToGain(decibelAdjustment);
  const isVolumeZero = volume === 0;

  // early exit if volume is zero
  if (isVolumeZero) return;

  // use Path2D for better performance with many rectangles
  const regularPath = new Path2D();
  const clippingPath = new Path2D();

  for (let barIndex = 0; barIndex < numBars; barIndex++) {
    const x = barIndex * BAR_TOTAL;

    // map bar index to peak data index
    const peakIndex = Math.floor((barIndex / numBars) * peaks.length);
    const peak = peaks[peakIndex] || 0;

    const scaledPeak = peak * volume;
    const isClipping = scaledPeak > 1;

    const barHeight = Math.max(0, Math.min(height, scaledPeak * height));

    if (barHeight === 0) continue;

    const barY = Math.round(height - barHeight);
    const roundedBarHeight = Math.round(barHeight);

    // add rectangle to regular path
    regularPath.rect(x, barY, BAR_WIDTH, roundedBarHeight);

    // add clipping indicator
    if (isClipping) {
      const clipIndicatorHeight = Math.min(2, roundedBarHeight);
      clippingPath.rect(x, barY, BAR_WIDTH, clipIndicatorHeight);
    }
  }

  // Draw all bars in two batch operations
  ctx.fillStyle = color;
  ctx.fill(regularPath);

  // Draw clipping indicators if any exist
  if (clippingPath) {
    ctx.fillStyle = CLIPPING_COLOR;
    ctx.fill(clippingPath);
  }
}

const WaveformCanvas = ({
  peaks,
  color,
  width,
  height,
  leftOffset,
  volume,
}: {
  peaks: Float32Array;
  color: string;
  width: number;
  height: number;
  leftOffset: number;
  volume: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const style = useMemo(() => ({ marginLeft: leftOffset }), [leftOffset]);

  useEffect(() => {
    const { current: canvasElement } = canvasRef;
    if (!canvasElement) return;

    canvasElement.width = width;
    canvasElement.height = height;

    drawPeaks(canvasElement, peaks, color, volume, width);
  }, [color, peaks, volume, width, height]);

  return (
    <canvas
      className="pointer-events-none"
      ref={canvasRef}
      style={style}
    />
  );
};

function TimelineWaveformBackground({
  children,
  waveformHeight,
  trackMuted,
  item,
}: {
  children?: React.ReactNode;
  waveformHeight: number;
  trackMuted: boolean;
  item: EditorStarterItem;
}) {
  const style = useMemo(
    () => ({
      height: waveformHeight,
      backgroundColor:
        item?.type === 'audio' ? ITEM_COLORS.audio : ITEM_COLORS.video,
    }),
    [waveformHeight, item.type],
  );

  return (
    <div
      className="group/timeline-item absolute bottom-0 flex w-full items-center overflow-hidden bg-editor-starter-panel"
      style={style}
    >
      <div
        className={clsx('relative h-full w-full', trackMuted && 'opacity-30')}
      >
        {children}
      </div>
    </div>
  );
}

const audioPeaksCache = new QuickLRU<string, AudioDataWithPeaks>({
  maxSize: 50,
});

export const TimelineItemWaveform = ({
  item,
  children,
  trackHeight,
  timelineWidth,
  roundedDifference,
  trackMuted,
}: {
  item: AudioItem | VideoItem;
  children: React.ReactNode;
  trackHeight: number;
  timelineWidth: number;
  roundedDifference: number;
  trackMuted: boolean;
}) => {
  const [audioData, setAudioData] = useState<AudioDataWithPeaks | null>(null);

  const { fps } = useFps();
  const { durationInFrames } = useTimelineContext();
  const visibleFrames = getVisibleFrames({
    fps: fps,
    totalDurationInFrames: durationInFrames,
  });
  const pixelsPerFrame = timelineWidth / visibleFrames;

  const asset = useAssetFromItem(item);

  const url = usePreferredLocalUrl(asset);
  const isLocalUrl = url.startsWith('blob:');
  // 오디오 전용 에셋과 단일 재생 권위인 립싱크 영상은 CDN URL에서도 파형을 생성한다.
  // 일반 비디오 파형은 성능 이슈 때문에 기존 정책대로 로컬 캐시에서만 허용한다.
  const shouldFetchWaveform =
    isLocalUrl ||
    asset.type === 'audio' ||
    (item.type === 'video' && hasEmbeddedLipsyncAudio(item));

  useEffect(() => {
    if (url && shouldFetchWaveform) {
      const cached = audioPeaksCache.get(url);

      if (cached) {
        setAudioData(cached);
        return;
      }

      getAudioData(url)
        .then((newAudioData) => {
          const peakData = generateDownsampledPeakData(
            newAudioData.channelWaveforms[0]!,
          );

          const audioDataWithPeaks: AudioDataWithPeaks = {
            ...newAudioData,
            peakData: peakData,
          };

          audioPeaksCache.set(url, audioDataWithPeaks);
          setAudioData(audioDataWithPeaks);
        })
        .catch(console.error);
    }
    // asset.type은 생명주기 동안 불변이지만 안전을 위해 deps 포함
  }, [url, shouldFetchWaveform, asset.type]);

  if (asset.type !== 'audio' && asset.type !== 'video') {
    throw new Error('Asset is not an audio or video');
  }

  const actualAudioFileDuration =
    audioData?.durationInSeconds ?? asset.durationInSeconds;
  const totalFrames = actualAudioFileDuration * fps;

  const waveformWidthPx = (totalFrames / item.playbackRate) * pixelsPerFrame;

  const startOffsetFrames =
    item.type === 'audio'
      ? item.audioStartFromInSeconds * fps
      : item.videoStartFromInSeconds * fps;

  const marginLeft =
    -(startOffsetFrames / item.playbackRate) * pixelsPerFrame -
    roundedDifference;

  const volume = item.decibelAdjustment;

  const waveformHeight = getWaveformHeight({
    item,
    trackHeight,
  });

  const itemWidthPx = item.durationInFrames * pixelsPerFrame;

  const height = getWaveformHeight({
    item,
    trackHeight,
  });

  // Calculate optimal peak level based on visual width
  const optimalPeakLevel = useMemo(() => {
    if (!audioData) return null;

    return selectPeakLevel(audioData.peakData, waveformWidthPx);
  }, [audioData, waveformWidthPx]);

  if (!optimalPeakLevel) {
    return (
      <TimelineWaveformBackground
        waveformHeight={waveformHeight}
        trackMuted={trackMuted}
        item={item}
      />
    );
  }

  return (
    <TimelineWaveformBackground
      waveformHeight={waveformHeight}
      trackMuted={trackMuted}
      item={item}
    >
      {FEATURE_AUDIO_FADE_CONTROL && (
        <>
          <FadeCurve
            item={item}
            height={waveformHeight}
            width={itemWidthPx}
            fadeType="audio"
          />
        </>
      )}
      <WaveformCanvas
        peaks={optimalPeakLevel.peaks}
        // @TODO: aduio가 아닐 떄 반응형 추가해야 됨
        color={item.type === 'audio' ? '#FFAA00' : '#6C7185'}
        width={waveformWidthPx}
        height={height}
        leftOffset={marginLeft}
        volume={volume}
      />
      {children}
    </TimelineWaveformBackground>
  );
};
